import { createFileRoute } from "@tanstack/react-router";
import { CalendarScreen } from "../screens/CalendarScreen";
import { isCalendarDate } from "../lib/month";

/**
 * `/calendar?d=YYYY-MM-DD` — the selected day, and with it the month on screen.
 *
 * **One search param, because there is one fact.** The month is derived from
 * `d` rather than carried beside it; two would be two things that can disagree,
 * and the pair that disagrees is the one where the grid shows March and the
 * list under it shows a day in April.
 *
 * **No `d` means today**, which is the ten-second case the whole app is built
 * around — the calendar tab has to open on the answer, not on a date picker.
 * Today is resolved by the screen rather than here, because it is a question
 * about the household's zone and this route has no session to read it from.
 *
 * A `d` that does not name a real day is **dropped, not rejected**. That is the
 * same call `convex/` makes for an id off a URL: a mistyped link should land on
 * something sensible rather than throw, and here the sensible thing already
 * exists. `2026-02-30` is the case that makes this more than a shape check —
 * see `isCalendarDate`.
 *
 * **`{ d: undefined }`, not `{}`, and that is load-bearing.** The router merges
 * this return over the raw search rather than replacing it, so omitting the key
 * does not drop it — it leaves the unvalidated value in place and hands it
 * straight to the screen. Measured, because it fails silently and only at the
 * edges: `?d=2026-02-30` reached `CalendarScreen` intact and produced a header
 * reading "mars 2026" above February's grid with no cell selected, since
 * `monthOf` slices the string and `Date.parse` rolls it forward. Setting the
 * key explicitly is what removes it — and the router then also strips it from
 * the address bar, so a bad link corrects itself.
 */
export const Route = createFileRoute("/calendar")({
  validateSearch: (search: Record<string, unknown>): { d?: string } =>
    isCalendarDate(search.d) ? { d: search.d } : { d: undefined },
  component: CalendarScreen,
});
